// src/theme.ts
// 深色×ゴールドの「プレミアム資格アプリ」テーマ。
// トークンは src/theme/tokens.ts に一元化。フォントは src/theme/fonts.ts。
import { createTheme } from '@mui/material/styles';
import { colors, radius, shadows } from './theme/tokens';
import { FONT_SANS, FONT_SERIF } from './theme/fonts';

const theme = createTheme({
    palette: {
        mode: 'dark',
        primary: {
            main: colors.gold,
            light: colors.goldBright,
            dark: colors.goldDeep,
            contrastText: '#0E1116',
        },
        secondary: {
            main: '#8A94A6', // 落ち着いたスレート（副次アクション）
            contrastText: '#0E1116',
        },
        success: { main: colors.success },
        error: { main: colors.error },
        warning: { main: colors.warning },
        info: { main: colors.info },
        background: {
            default: colors.bg,
            paper: colors.surface,
        },
        text: {
            primary: colors.textPrimary,
            secondary: colors.textSecondary,
            disabled: colors.textDisabled,
        },
        divider: colors.border,
    },
    shape: {
        borderRadius: radius.md,
    },
    typography: {
        fontFamily: FONT_SANS,
        // 見出しは明朝で権威・上品さを出す
        h1: { fontFamily: FONT_SERIF, fontWeight: 700, letterSpacing: '0.01em' },
        h2: { fontFamily: FONT_SERIF, fontWeight: 700, letterSpacing: '0.01em' },
        h3: { fontFamily: FONT_SERIF, fontWeight: 700, letterSpacing: '0.01em' },
        h4: { fontFamily: FONT_SERIF, fontWeight: 600, letterSpacing: '0.01em' },
        h5: { fontWeight: 700 },
        h6: { fontWeight: 700 },
        button: { fontWeight: 600, textTransform: 'none', letterSpacing: '0.02em' },
        // 数字はタブラーで桁揃え（スコア等）
        body1: { fontFeatureSettings: '"tnum" 1' },
    },
    components: {
        MuiCssBaseline: {
            styleOverrides: {
                body: {
                    backgroundColor: colors.bg,
                    WebkitFontSmoothing: 'antialiased',
                    MozOsxFontSmoothing: 'grayscale',
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    backgroundColor: colors.surface,
                    backgroundImage: 'none',
                    border: `1px solid ${colors.borderNeutral}`,
                    borderRadius: radius.lg,
                    boxShadow: shadows.card,
                },
            },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    backgroundImage: 'none',
                },
                // 面の階層をelevationで表現
                outlined: {
                    borderColor: colors.borderNeutral,
                },
            },
        },
        MuiButton: {
            defaultProps: { disableElevation: true },
            styleOverrides: {
                root: {
                    borderRadius: radius.sm,
                    paddingInline: 18,
                },
                containedPrimary: {
                    color: '#0E1116',
                    background: `linear-gradient(135deg, ${colors.goldBright}, ${colors.goldDeep})`,
                    '&:hover': {
                        background: `linear-gradient(135deg, ${colors.gold}, ${colors.goldDeep})`,
                    },
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: { borderRadius: radius.sm, fontWeight: 600 },
            },
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    '& .MuiOutlinedInput-root': {
                        backgroundColor: 'rgba(255, 255, 255, 0.04)',
                        borderRadius: radius.sm,
                        '& fieldset': { borderColor: colors.borderNeutral },
                        '&:hover fieldset': { borderColor: 'rgba(228,185,91,0.4)' },
                        '&.Mui-focused fieldset': { borderColor: colors.gold },
                    },
                },
            },
        },
        MuiSelect: {
            styleOverrides: {
                root: {
                    backgroundColor: 'rgba(255, 255, 255, 0.04)',
                    borderRadius: radius.sm,
                },
            },
        },
        MuiListItem: {
            styleOverrides: {
                root: { '&.MuiListItem-root': { backgroundColor: 'transparent' } },
            },
        },
        MuiAccordion: {
            styleOverrides: {
                root: {
                    backgroundColor: colors.surface,
                    backgroundImage: 'none',
                    border: `1px solid ${colors.borderNeutral}`,
                    borderRadius: radius.md,
                    '&:before': { display: 'none' },
                },
            },
        },
    },
});

export default theme;
